/**
 * AnalystsPage - List of all analysts with search and sector filter.
 * Click a row to navigate to /analyst/:id detail page.
 */

import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { useLanguage } from '../contexts/LanguageContext';
import { getAnalysts, AnalystListItem, prefetchAnalystProfile } from '../services/v3RagService';

const AnalystsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [analysts, setAnalysts] = useState<AnalystListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [sectorFilter, setSectorFilter] = useState<string>('');

  useEffect(() => {
    getAnalysts()
      .then(data => setAnalysts(data))
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const sectors = useMemo(() => {
    const s = new Set<string>();
    analysts.forEach(a => { if (a.sector_focus) s.add(a.sector_focus); });
    return Array.from(s).sort();
  }, [analysts]);

  const filtered = useMemo(() => {
    let list = analysts;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(a =>
        a.name.toLowerCase().includes(q) ||
        (a.firm && a.firm.toLowerCase().includes(q))
      );
    }
    if (sectorFilter) {
      list = list.filter(a => a.sector_focus === sectorFilter);
    }
    return list;
  }, [analysts, search, sectorFilter]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>
        {t('nav.analysts')}
      </Typography>

      {/* Filters */}
      <Box sx={{
        display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap',
        position: 'sticky', top: 0, zIndex: 10,
        bgcolor: 'background.default', py: 1,
      }}>
        <TextField
          size="small"
          placeholder={t('common.search')}
          value={search}
          onChange={e => setSearch(e.target.value)}
          autoComplete="off"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: { xs: '100%', sm: 250 } }}
        />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>{t('table.sector')}</InputLabel>
          <Select
            value={sectorFilter}
            label={t('table.sector')}
            onChange={e => setSectorFilter(e.target.value)}
          >
            <MenuItem value="">{t('common.all')}</MenuItem>
            {sectors.map(s => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
          {filtered.length} {t('nav.analysts').toLowerCase()}
        </Typography>
      </Box>

      {/* Table */}
      <Paper elevation={2}>
        <TableContainer sx={{ minHeight: 400 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Firm</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{t('table.sector')}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Documents</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Last Report</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>II Ranking</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map(a => (
                <TableRow
                  key={a.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/analyst/${a.id}`)}
                  onMouseEnter={() => prefetchAnalystProfile(a.id)}
                >
                  <TableCell sx={{ fontWeight: 600 }}>{a.name}</TableCell>
                  <TableCell>{a.firm || '-'}</TableCell>
                  <TableCell>
                    {a.sector_focus ? (
                      <Chip label={a.sector_focus} size="small" variant="outlined" />
                    ) : '-'}
                  </TableCell>
                  <TableCell>{a.document_count ?? '-'}</TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {a.last_document_date || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {a.ii_ranking ? (
                      <Chip label={`#${a.ii_ranking}`} size="small" color="primary" variant="outlined" />
                    ) : '-'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default AnalystsPage;
